import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { customerData } from '../../utilities/CustomerData'
import { refreshAccessToken } from '../../utilities/extensions'
import { apiURL } from '../../utilities/URLs'

const ESIGN_ORIGIN = 'https://www.inkavella.com'

export default function Withdrawal8821Ack() {
  const navigate = useNavigate()
  const leadId = customerData.customerLeadData.leadId
  const [signingUrl, setSigningUrl] = useState('')
  const [loading, setLoading] = useState(true)
  const [signingComplete, setSigningComplete] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [notRequired, setNotRequired] = useState(false)
  const [error, setError] = useState('')

  const completeAck = useCallback(async () => {
    setSubmitting(true)
    try {
      const response = await fetch(`${apiURL}/api/completeWithdrawal8821Ack?leadId=${encodeURIComponent(leadId)}`, {
        method: 'POST',
        headers: { Authorization: customerData.accessToken }
      })
      const data = await response.json()
      if (data.response?.error) {
        if (data.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? completeAck() : navigate('/', { state: { expired: true } })
        } else
          setError(data.response.error)
      }
    } catch (_) {
      setError('Could not finalize your signature. Please contact support if this continues.')
    }
    setSubmitting(false)
  }, [leadId, navigate])

  const loadSession = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const response = await fetch(`${apiURL}/api/getWithdrawal8821AckEmbedURL?leadId=${encodeURIComponent(leadId)}`, {
        method: 'POST',
        headers: { Authorization: customerData.accessToken }
      })
      const data = await response.json()
      if (data.response?.error) {
        if (data.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? loadSession() : navigate('/', { state: { expired: true } })
        } else
          setError(data.response.error)
      } else if (!data.response?.required || data.response?.already_signed) {
        setNotRequired(true)
      } else if (data.response?.signingUrl) {
        setSigningUrl(data.response.signingUrl)
      } else
        setError('Unable to load the signing form.')
    } catch (_) {
      setError('Unable to load the signing form.')
    }
    setLoading(false)
  }, [leadId, navigate])

  useEffect(() => {
    document.title = 'SETC Pros | Withdrawal Acknowledgment'
    loadSession()

    const handleMessage = (event) => {
      if (event.origin !== ESIGN_ORIGIN) return
      if (event.data?.type === 'esign_complete') {
        setSigningComplete(true)
        completeAck()
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [loadSession, completeAck])

  return (
    <div className='min-h-screen bg-[#F5F7FA] flex flex-col items-center px-4 py-10 font-mont'>
      <div className='w-full max-w-3xl bg-white rounded-2xl shadow-md p-6 flex flex-col gap-4'>
        <h1 className='text-2xl font-bold text-[#333] text-center'>Form 8821 Withdrawal Acknowledgment</h1>
        <p className='text-sm text-[#666] text-center'>
          Please review and sign to confirm you understand that SETC Pros / Turnkey will withdraw Form 8821 with the IRS.
        </p>

        {loading && <p className='text-center text-gray-500'>Loading signing form…</p>}
        {error && <p className='text-center text-red-600 text-sm'>{error}</p>}

        {notRequired && !loading && (
          <div className='text-center flex flex-col gap-4 items-center'>
            <p className='text-green-700 font-semibold'>No signature is required right now.</p>
            <button onClick={() => navigate('/dashboard')} className='px-6 py-3 bg-[#5186C0] text-white rounded-lg font-semibold'>
              Back to Dashboard
            </button>
          </div>
        )}

        {signingComplete && (
          <div className='text-center flex flex-col gap-4 items-center'>
            <p className='text-green-700 font-semibold'>Thank you — your signature was received.</p>
            {submitting && <p className='text-sm text-gray-500'>Finalizing…</p>}
            <button onClick={() => navigate('/dashboard')} className='px-6 py-3 bg-[#5186C0] text-white rounded-lg font-semibold'>
              Back to Dashboard
            </button>
          </div>
        )}

        {!loading && !notRequired && !signingComplete && signingUrl && (
          <iframe
            title='Form 8821 Withdrawal Acknowledgment'
            src={signingUrl}
            className='w-full h-[70vh] border border-gray-200 rounded-xl'
            allow='camera; microphone'
          />
        )}
      </div>
    </div>
  )
}
